import {createContext, useContext, useEffect, useState, ReactNode} from "react";
import {useDispatch} from "react-redux";

import {setBackendError} from "@/actions";
import {Permission} from "@/authSchema";
import {PredefinedContextName, PredefinedRoleName} from "@/predefinedAuthNames";
import {useUserRolesQuery} from "./queries.generated";
import {useUserManager} from "./Login";
import {User} from "oidc-client-ts";
import {concertoLocalStorage} from "@/utils/localStorage";

const AuthContext = createContext(undefined);

type Context = {
    data: Record<string, any>;
    name: string;
};

type Role = {
    contexts: Context[];
    name: string;
    permissions: Permission[];
};

export type ContextFilter = (context: Context) => boolean;

export class ContextFilters {
    static domain(domainName: string): ContextFilter {
        return (context: Context) =>
            context.name === PredefinedContextName.domain && [domainName, ""].includes(context.data.name);
    }

    static extension(extensionShortNumber: string, domainName: string): ContextFilter {
        return (context: Context) =>
            ContextFilters.domain(domainName)(context) ||
            (context.name === PredefinedContextName.extension &&
                [extensionShortNumber + "@" + domainName].includes(context.data.identifier));
    }
}

/// provides authorization information deduced from the roles of the user
export class Auth {
    private _roles: Role[];
    private _accessToken: string | null;

    constructor(roles: Role[], accessToken: string | null = null) {
        this._roles = roles;
        this._accessToken = accessToken;
    }

    get accessToken(): string | null {
        return this._accessToken;
    }

    get roles() {
        return this._roles;
    }

    get isSuperuser(): boolean {
        return this._roles.some((r) => r.name === "superuser");
    }

    get personalExtensionIdentifiers(): string[] {
        const res = [];
        for (const role of this.roles) {
            for (const ctx of role.contexts) {
                if (ctx.name === PredefinedContextName.extension && ctx.data.identifier !== "") {
                    res.push(ctx.data.identifier);
                }
            }
        }
        return res;
    }

    /** Check if the user has the given permission in one of their roles
     * @param permission the permission to check
     * @param contextFilter optional filter that will be applied to the context of the role that has the permission (see ContextFilters for predefined filters)
     * @returns true if the user has the permission in one of their roles and the context filter is satisfied
     **/
    hasPermission(permission: Permission, contextFilter?: ContextFilter): boolean {
        return this._roles.some((role) => {
            if (!role.permissions.includes(permission)) {
                return false;
            }

            if (contextFilter) {
                return role.contexts.some(contextFilter);
            }

            return true;
        });
    }

    hasRole(name: keyof typeof PredefinedRoleName, contextFilter?: ContextFilter): boolean {
        return this._roles.some((role) => {
            if (role.name !== PredefinedRoleName[name]) {
                return false;
            }

            if (contextFilter) {
                return role.contexts.some(contextFilter);
            }

            return true;
        });
    }
}

export function useAuth(): Auth {
    return useContext(AuthContext);
}

/**
 * Context provider that fetches the roles from Heimdall.
 *
 * Use useAuth() to access the authorization information.
 */
export const AuthProvider = ({children}: {children: ReactNode}) => {
    const [auth, setAuth] = useState<Auth>(null);
    const [accessToken, setAccessToken] = useState<string | null>(null);

    const dispatch = useDispatch();
    const userManager = useUserManager();

    // Fetch access token from UserManager and set up event listeners
    useEffect(() => {
        const fetchAccessToken = async () => {
            try {
                const user = await userManager.getUser();
                if (user && user.access_token) {
                    setAccessToken(user.access_token);
                } else {
                    setAccessToken(null);
                }
            } catch (error) {
                console.error("Failed to get access token from UserManager:", error);
                setAccessToken(null);
            }
        };

        // Initial fetch
        fetchAccessToken();

        // Set up event listeners for user manager events
        const handleUserLoaded = (user: User) => {
            if (user) {
                if (user.access_token) {
                    setAccessToken(user.access_token);
                }

                const localProfile = concertoLocalStorage.get("profile");
                if (localProfile.locale !== user.profile.locale) {
                    concertoLocalStorage.set("profile", {...localProfile, locale: user.profile.locale});
                }
            } else {
                setAccessToken(null);
            }
        };
        const handleUserUnloaded = () => setAccessToken(null);

        userManager.events.addUserLoaded(handleUserLoaded);
        userManager.events.addUserUnloaded(handleUserUnloaded);

        return () => {
            userManager.events.removeUserLoaded(handleUserLoaded);
            userManager.events.removeUserUnloaded(handleUserUnloaded);
        };
    }, [userManager]);

    const roles = useUserRolesQuery({
        fetchPolicy: "no-cache", // Auth data should always be fresh and not cached
        skip: !accessToken, // Skip if no access token is available
    });

    // Side-effect for handling query errors (replacement for deprecated onError option)
    useEffect(() => {
        if (roles.error) {
            console.error("Failed to fetch user roles:", roles.error);
            dispatch(setBackendError({code: "cant_reach_heimdall"}));
        }
    }, [roles.error, dispatch]);

    // Handle roles data updates (both initial load and refetch)
    useEffect(() => {
        if (roles.data && accessToken) {
            const mappedRoles = roles.data.userRoles.map((role) => ({
                name: role.name,
                contexts: role.contexts.map((ctx) => ({
                    name: ctx.name,
                    data: ctx.data,
                })),
                permissions: role.permissions as Permission[],
            }));

            setAuth(new Auth(mappedRoles, accessToken));
        }
    }, [roles.data, accessToken]);

    // Refetch user roles when accessToken changes
    useEffect(() => {
        if (accessToken && roles.refetch) {
            console.debug("Refetching user roles due to access token change");
            roles.refetch();
        }
    }, [accessToken, roles]);

    return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};
